import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../api/adminApi";
import { AdminLayout } from "../../components/layouts";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  TabButton,
  Tabs,
  Textarea,
  Toast,
} from "../../components/ui";
import Modal from "../../components/Modal";

const QUEUE_PAGE_SIZE = 10;
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";
const TABS = [{ key: "payouts", label: "Payouts" }, { key: "refunds", label: "Refunds" }];

const AdminPayments = ({ embedded = false, forcedTab = null }) => {
  const [tab, setTab] = useState(forcedTab || "payouts");
  const [queuePage, setQueuePage] = useState(1);
  const [data, setData] = useState({ refunds: [], payouts: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundFor, setRefundFor] = useState(null);

  useEffect(() => {
    if (forcedTab) { setTab(forcedTab); setQueuePage(1); }
  }, [forcedTab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.paymentQueues({ type: tab, page: queuePage, limit: QUEUE_PAGE_SIZE });
      const next = res.data || { refunds: [], payouts: [], pagination: {} };
      setData(next);
      const actualPage = next.pagination?.[tab]?.page;
      if (actualPage && actualPage !== queuePage) setQueuePage(actualPage);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load payments.");
    } finally { setLoading(false); }
  }, [queuePage, tab]);

  useEffect(() => { load(); }, [load]);

  const flash = (message) => { setToast(message); setTimeout(() => setToast(""), 2500); };
  const release = async (id, canRelease) => {
    if (!canRelease) return;
    setBusy(`rel:${id}`);
    try { await adminApi.releasePayout(id); flash("Payout released."); await load(); }
    catch (err) { flash(err.response?.data?.message || "Release failed."); }
    finally { setBusy(""); }
  };
  const submitRefund = async (amount, note) => {
    if (!refundFor) return;
    setRefundBusy(true);
    try { await adminApi.refundBooking(refundFor._id, { amount, note }); flash("Refund processed."); setRefundFor(null); await load(); }
    catch (err) { flash(err.response?.data?.message || "Refund failed."); }
    finally { setRefundBusy(false); }
  };

  const payouts = data.payouts || [];
  const refunds = data.refunds || [];
  const pagination = data.pagination?.[tab] || { page: queuePage, pages: 1, total: 0, limit: QUEUE_PAGE_SIZE };
  const switchTab = (nextTab) => { if (nextTab !== tab) { setTab(nextTab); setQueuePage(1); } };

  const Pager = () => pagination.pages > 1 ? (
    <div className="flex flex-wrap items-center justify-center gap-3 border-t border-slate-100 px-4 py-4">
      <Button size="sm" variant="outline" disabled={pagination.page <= 1 || loading} onClick={() => setQueuePage((p) => Math.max(1, p - 1))}>Previous</Button>
      <span className="font-dm text-xs text-giggs-muted">Page {pagination.page} of {pagination.pages} · {pagination.total} total</span>
      <Button size="sm" variant="outline" disabled={pagination.page >= pagination.pages || loading} onClick={() => setQueuePage((p) => Math.min(pagination.pages, p + 1))}>Next</Button>
    </div>
  ) : null;

  const content = (
    <div className="font-dm">
      {toast && <Toast>{toast}</Toast>}
      {!forcedTab && (
        <Tabs className="mb-4">
          {TABS.map((item) => <TabButton key={item.key} active={tab === item.key} count={tab === item.key ? pagination.total : undefined} onClick={() => switchTab(item.key)}>{item.label}</TabButton>)}
        </Tabs>
      )}
      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

      {tab === "payouts" && (
        <Card className="overflow-hidden shadow-none">
          <CardHeader title="Gigger payouts" description="Release earnings only after the client has paid   and the Gigger payout account is ready." action={<Badge variant="neutral">{pagination.total}</Badge>} />
          {loading ? <EmptyState title="Loading…" /> : payouts.length === 0 ? <EmptyState title="No payouts waiting." /> : (
            <div className="divide-y divide-slate-100">
              {payouts.map((payout) => {
                const released = payout.payoutStatus === "released";
                const canRelease = payout.clientPaidInFull && payout.payoutAccountReady && !released;
                const blockReason = released ? null : !payout.clientPaidInFull ? "Client hasn't paid  " : !payout.payoutAccountReady ? "Gigger payout account not ready" : null;
                return (
                  <div key={payout._id} className="grid gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:px-5">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-giggs-ink">{payout.gigger?.name || "—"}</div>
                      <div className="mt-0.5 break-words text-xs text-giggs-muted">{payout.trackingId} · {payout.service?.name} · client {payout.clientName}</div>
                    </div>
                    <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:gap-1">
                      <Badge variant={released ? "success" : "warning"}>{released ? "Released" : "Awaiting release"}</Badge>
                      <strong className="text-sm text-giggs-green">{money(payout.giggerEarnings)}</strong>
                    </div>
                    <div className="sm:min-w-[150px] sm:text-right">
                      {released ? <span className="text-xs text-giggs-muted">{payout.releasedAt ? `Released ${fmtDate(payout.releasedAt)}` : "Released"}</span> : (
                        <>
                          <Button size="sm" className="w-full sm:w-auto" disabled={!canRelease || busy === `rel:${payout._id}`} onClick={() => release(payout._id, canRelease)} title={blockReason || "Release payout"}>{busy === `rel:${payout._id}` ? "Releasing…" : "Release payout"}</Button>
                          {blockReason && <div className="mt-1 text-[11px] text-amber-700">{blockReason}</div>}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!loading && <Pager />}
        </Card>
      )}

      {tab === "refunds" && (
        <Card className="overflow-hidden shadow-none">
          <CardHeader title="Client refunds" description="Cancelled jobs with money collected from the client appear here for full or partial refund." action={<Badge variant="neutral">{pagination.total}</Badge>} />
          {loading ? <EmptyState title="Loading…" /> : refunds.length === 0 ? <EmptyState title="No cancelled bookings with collected funds." /> : (
            <div className="divide-y divide-slate-100">
              {refunds.map((row) => (
                <div key={row._id} className="grid gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:px-5">
                  <div className="min-w-0"><div className="truncate text-sm font-bold text-giggs-ink">{row.clientName || "—"}</div><div className="mt-0.5 break-words text-xs text-giggs-muted">{row.trackingId} · {row.service?.name} · gigger {row.gigger?.name}</div></div>
                  <div className="text-xs text-giggs-text sm:min-w-[150px] sm:text-right"><div>Collected <strong>{money(row.collected)}</strong></div><div>Refunded <strong>{money(row.refunded)}</strong></div></div>
                  <div className="sm:min-w-[140px] sm:text-right">{row.fullyRefunded ? <Badge variant="success">Fully refunded</Badge> : <Button size="sm" variant="danger" className="w-full sm:w-auto" onClick={() => setRefundFor(row)}>Refund client</Button>}</div>
                </div>
              ))}
            </div>
          )}
          {!loading && <Pager />}
        </Card>
      )}

      {refundFor && <RefundModal row={refundFor} busy={refundBusy} onClose={() => setRefundFor(null)} onRefund={submitRefund} />}
    </div>
  );

  if (embedded) return content;
  return <AdminLayout title="Payments">{content}</AdminLayout>;
};

const RefundModal = ({ row, busy, onClose, onRefund }) => {
  const [mode, setMode] = useState("full");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const partialInvalid = mode === "partial" && (!amount || Number(amount) <= 0 || Number(amount) > row.refundable);
  const submit = () => {
    if (mode === "partial") {
      const n = Number(amount);
      if (!Number.isFinite(n) || n <= 0) return;
      onRefund(n, note);
    } else onRefund(undefined, note);
  };

  return (
    <Modal title="Refund client" size="sm" onClose={onClose}>
      <div className="flex flex-col gap-4 p-5 sm:p-6">
        <div className="text-xs text-giggs-muted">{row.trackingId} · {row.clientName}</div>
        <Card className="p-4 shadow-none"><ValueRow label="Collected from client" value={money(row.collected)} /><ValueRow label="Already refunded" value={money(row.refunded)} /><ValueRow label="Refundable now" value={money(row.refundable)} bold /></Card>
        <div className="grid grid-cols-2 gap-2"><Button variant={mode === "full" ? "primary" : "outline"} onClick={() => setMode("full")}>Full ({money(row.refundable)})</Button><Button variant={mode === "partial" ? "primary" : "outline"} onClick={() => setMode("partial")}>Partial</Button></div>
        {mode === "partial" && <Field label="Amount (AUD)" error={amount && Number(amount) > row.refundable ? `Can't exceed ${money(row.refundable)}.` : undefined}><Input type="number" min="0" step="0.01" max={row.refundable} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" /></Field>}
        <Field label="Note (optional)"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / reference…" /></Field>
        <Button variant="danger" className="w-full" disabled={busy || partialInvalid} onClick={submit}>{busy ? "Processing…" : mode === "full" ? `Refund ${money(row.refundable)}` : `Refund ${amount ? money(Number(amount)) : "$0.00"}`}</Button>
      </div>
    </Modal>
  );
};

const ValueRow = ({ label, value, bold }) => <div className="flex items-center justify-between gap-3 py-1"><span className={`text-giggs-muted ${bold ? "text-sm font-bold" : "text-xs font-medium"}`}>{label}</span><strong className={`${bold ? "text-base text-giggs-green" : "text-xs text-giggs-ink"}`}>{value}</strong></div>;

export default AdminPayments;
